<script setup>
// WEXHUB: seccion propia de Configuracion para las plantillas de WhatsApp.
// Antes vivia dentro de los ajustes de cada buzon, donde no se encontraba.
// Si hay mas de una cuenta de WhatsApp se elige cual; con una sola se
// selecciona sola y el selector no se muestra.
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import WhatsappTemplatesAPI from 'dashboard/api/whatsappTemplates';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import AddTemplate from './AddTemplate.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

defineOptions({
  name: 'WhatsappTemplatesSettings',
});

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const selectedInboxId = ref(null);
const templates = ref([]);
const isLoading = ref(false);
const showAddPopup = ref(false);

// Solo Cloud API: 360dialog y Twilio administran sus plantillas por su propio
// panel, asi que ofrecerlos aca seria mentira. 360dialog llega como provider
// "default", por eso se pide whatsapp_cloud en vez de excluir "360dialog".
const whatsappInboxes = computed(() =>
  getters['inboxes/getInboxes'].value.filter(
    inbox =>
      inbox.channel_type === 'Channel::Whatsapp' &&
      inbox.provider === 'whatsapp_cloud'
  )
);

const selectedInbox = computed(() =>
  whatsappInboxes.value.find(inbox => inbox.id === selectedInboxId.value)
);

const fetchTemplates = async () => {
  if (!selectedInboxId.value) return;
  isLoading.value = true;
  try {
    const { data } = await WhatsappTemplatesAPI.getTemplates(
      selectedInboxId.value
    );
    templates.value = data.payload || [];
  } catch (error) {
    useAlert(t('INBOX_MGMT.WHATSAPP_TEMPLATES.LIST_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const removeTemplate = async name => {
  try {
    await WhatsappTemplatesAPI.deleteTemplate(selectedInboxId.value, name);
    useAlert(t('INBOX_MGMT.WHATSAPP_TEMPLATES.DELETED'));
    await fetchTemplates();
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('INBOX_MGMT.WHATSAPP_TEMPLATES.DELETE_ERROR')
    );
  }
};

const statusClass = status => {
  if (status === 'APPROVED') return 'bg-n-teal-3 text-n-teal-11';
  if (status === 'REJECTED') return 'bg-n-ruby-3 text-n-ruby-11';
  return 'bg-n-amber-3 text-n-amber-11';
};

// Meta devuelve el estado en ingles y en mayusculas; se muestra traducido.
const statusLabel = status => {
  const labels = {
    APPROVED: t('INBOX_MGMT.WHATSAPP_TEMPLATES.STATUS.APPROVED'),
    PENDING: t('INBOX_MGMT.WHATSAPP_TEMPLATES.STATUS.PENDING'),
    REJECTED: t('INBOX_MGMT.WHATSAPP_TEMPLATES.STATUS.REJECTED'),
  };
  return labels[status] || status;
};

const bodyOf = template =>
  template.components?.find(component => component.type === 'BODY')?.text || '';

watch(
  whatsappInboxes,
  inboxes => {
    const stillExists = inboxes.some(i => i.id === selectedInboxId.value);
    if (!stillExists) selectedInboxId.value = inboxes[0]?.id ?? null;
  },
  { immediate: true }
);

watch(selectedInboxId, fetchTemplates, { immediate: true });

onMounted(() => {
  if (!whatsappInboxes.value.length) store.dispatch('inboxes/get');
});
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :loading-message="$t('WHATSAPP_TEMPLATE_MGMT.LOADING')"
    :no-records-found="!whatsappInboxes.length"
    :no-records-message="$t('WHATSAPP_TEMPLATE_MGMT.NO_INBOXES')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('WHATSAPP_TEMPLATE_MGMT.HEADER')"
        :description="$t('WHATSAPP_TEMPLATE_MGMT.DESCRIPTION')"
      >
        <template v-if="templates.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ $t('WHATSAPP_TEMPLATE_MGMT.COUNT', { n: templates.length }) }}
          </span>
        </template>
        <template #actions>
          <Button
            :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.NEW')"
            size="sm"
            :disabled="!selectedInbox"
            @click="showAddPopup = true"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <div class="flex flex-col gap-4">
        <label
          v-if="whatsappInboxes.length > 1"
          class="flex flex-col gap-1 max-w-sm"
        >
          <span class="text-body-main text-n-slate-11">
            {{ $t('WHATSAPP_TEMPLATE_MGMT.INBOX_SELECTOR') }}
          </span>
          <select v-model="selectedInboxId">
            <option
              v-for="inbox in whatsappInboxes"
              :key="inbox.id"
              :value="inbox.id"
            >
              {{ `${inbox.name} (${inbox.phone_number})` }}
            </option>
          </select>
        </label>

        <div
          v-if="!templates.length"
          class="flex flex-1 justify-center items-center py-20 text-base text-center text-body-main text-n-slate-11"
        >
          {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.EMPTY') }}
        </div>

        <!-- Mismo patron de filas que el listado de bandejas de entrada. -->
        <div v-else class="border-t divide-y divide-n-weak border-n-weak">
          <div
            v-for="template in templates"
            :key="`${template.name}-${template.language}`"
            class="flex flex-row gap-4 justify-between items-start py-4"
          >
            <div class="flex gap-4 items-start min-w-0">
              <span
                class="grid border shadow-sm place-items-center bg-n-alpha-3 rounded-xl size-10 ring ring-n-solid-1 border-n-strong shrink-0"
              >
                <Icon
                  icon="i-ph-whatsapp-logo"
                  class="size-5 text-n-slate-10"
                />
              </span>
              <div class="flex flex-col gap-1 items-start min-w-0">
                <span class="flex flex-wrap gap-2 items-center">
                  <span class="font-mono text-heading-3 text-n-slate-12">
                    {{ template.name }}
                  </span>
                  <span
                    class="px-2 py-0.5 text-xs font-medium rounded-full"
                    :class="statusClass(template.status)"
                  >
                    {{ statusLabel(template.status) }}
                  </span>
                  <span
                    class="px-2 py-0.5 text-xs uppercase rounded-full bg-n-alpha-2 text-n-slate-11"
                  >
                    {{ template.language }}
                  </span>
                </span>
                <p class="m-0 text-body-main text-n-slate-11 line-clamp-2">
                  {{ bodyOf(template) }}
                </p>
              </div>
            </div>
            <div class="flex gap-3 justify-end shrink-0">
              <Button
                v-tooltip.top="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.DELETE')"
                icon="i-woot-bin"
                slate
                sm
                class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                @click="removeTemplate(template.name)"
              />
            </div>
          </div>
        </div>
      </div>
    </template>

    <woot-modal
      v-model:show="showAddPopup"
      size="modal-big"
      :on-close="() => (showAddPopup = false)"
    >
      <AddTemplate
        v-if="selectedInbox"
        :inbox-id="selectedInbox.id"
        :on-close="() => (showAddPopup = false)"
        @created="fetchTemplates"
      />
    </woot-modal>
  </SettingsLayout>
</template>
